import type { AppThunk } from '../appThunk';
import { appendProjectsListPage, resetProjectsListPage, setProjectListLoadingMore } from '../slice/FJStartLandingSlice';
import { setFreeTimes, setTotalCredits, totalCreditsFromApi } from '../slice/FJCommonSlice';
import FJLongToShortStore from '@src/store/FJLongToShortStore';
import FJNetworkManager from '@src/core/FJNetworkManager';
import { GENERATE_STATE } from '../../constants/FJEnumConstant';
import FJConfig from '../../config/FJConfig';
import { FJStartLandingAction } from './FJStartLandingAction';
import FJRequirementsUtil from '@src/util/FJRequirementsUtil';
import FJShareSlice from '@src/redux/slice/FJShareSlice.ts';
import { ACCESS_TOKEN_STATUS, SHARE_BINDING_STATUS, SOCIAL_KEY } from '@src/constants/FJShareSocialConstant.ts';
import type { ProjectItem } from '../../types/FJProject';
import type { FJDetailsListItem } from '../../types/FJDetails';
import FJLocalStore from '@src/store/FJLocalStore';
import FJMessageStore, { MESSAGE_TYPE } from '@src/store/FJMessageStore';

export const FJCommonAction = {
    /**
     * 从第一页刷新当前已展示深度：limit = max(FJConfig.TASK_LIST_PAGE_SIZE, 当前列表长度)，兼顾轮询与增删。
     */
    fetchTaskListAction: (): AppThunk => async (dispatch, getState) => {
        const loaded = getState().startLanding.projects.items.length;
        const limit = Math.max(FJConfig.TASK_LIST_PAGE_SIZE, loaded);
        const page = await FJLongToShortStore.getTaskListPage({ offset: 1, limit });
        if (page != null) {
            dispatch(
                resetProjectsListPage({
                    items: page.items,
                    nextOffset: page.nextOffset,
                    hasMore: page.hasMore,
                    total: page.total,
                }),
            );

            const proIds = page.items.reduce<string[]>((acc, item: ProjectItem) => {
                if (item.status === GENERATE_STATE.PROCESSING && item.eventId) {
                    return [...acc, item.eventId];
                }
                const ids = (item.details ?? [])
                    .filter((b: FJDetailsListItem) => b.status === GENERATE_STATE.PROCESSING)
                    .map((b: FJDetailsListItem) => ('subEventId' in b ? b.subEventId : undefined))
                    .filter((id): id is string => Boolean(id));
                return [...acc, ...ids];
            }, [] as string[]);
            if (proIds.length) {
                FJLongToShortStore.startPollingTasks(proIds, {
                    callback: () => {
                        dispatch(FJCommonAction.fetchTaskListAction());
                    },
                    progressCallback: ({ id, progress }: { id: string; progress: number }) => {
                        const p = Math.min(100, Math.round(progress * 100));
                        dispatch(FJStartLandingAction.updataProjectProgressAction(id, p));
                    },
                });
            }
        }
    },
    /** 向下滚动加载更多（每页条数见 FJConfig.TASK_LIST_PAGE_SIZE） */
    loadMoreTaskListAction: (): AppThunk => async (dispatch, getState) => {
        const { listHasMore, listLoadingMore, listNextOffset, items } = getState().startLanding.projects;
        if (!listHasMore || listLoadingMore || items.length === 0) {
            return;
        }
        dispatch(setProjectListLoadingMore(true));
        try {
            const page = await FJLongToShortStore.getTaskListPage({
                offset: listNextOffset,
                limit: FJConfig.TASK_LIST_PAGE_SIZE,
            });
            if (page != null) {
                dispatch(
                    appendProjectsListPage({
                        items: page.items,
                        nextOffset: page.nextOffset,
                        hasMore: page.hasMore,
                        total: page.total,
                    }),
                );
            } else {
                FJMessageStore.addMessage(FJLocalStore._('network-error-please-try-again'), MESSAGE_TYPE.ERROR);
            }
        } finally {
            dispatch(setProjectListLoadingMore(false));
        }
    },
    /** 拉取积分总额 → `common.totalCredits`；失败则不改 */
    fetchCreditsInfoAction: (): AppThunk => async dispatch => {
        try {
            const raw = await FJNetworkManager.getCreditsInfo();
            dispatch(setTotalCredits(totalCreditsFromApi(raw)));
        } catch {
            // 保持上次成功值或初始 0
        }
    },
    fetchFreeTimesAction: (): AppThunk => async dispatch => {
        try {
            const freeTimesData = await FJNetworkManager.getFreeTimes();
            const freeTimes = freeTimesData['ai-long-to-short'];
            dispatch(setFreeTimes(freeTimes ?? 0));
        } catch {
            // 保持上次成功值或初始 0
            dispatch(setFreeTimes(0));
        }
    },
    updateTotalCreditsAction: (): AppThunk => async (dispatch, getState) => {
        const { freeTimes } = getState().common;
        if (freeTimes > 0) {
            dispatch(FJCommonAction.fetchFreeTimesAction());
        }
        dispatch(FJCommonAction.fetchCreditsInfoAction());
    },
    initProjectAction:
        (callback: () => void): AppThunk =>
        async dispatch => {
            if (!FJRequirementsUtil.isLogin()) {
                callback?.();
                return;
            }
            dispatch(FJCommonAction.fetchCreditsInfoAction());
            dispatch(FJCommonAction.fetchFreeTimesAction());
            await dispatch(FJCommonAction.fetchTaskListAction());
            callback?.();
            const __updateSocialState = (shareType: string, data: any) => {
                dispatch(
                    FJShareSlice.actions.updateShareState({
                        shareType: shareType,
                        data: {
                            linkAccountState: SHARE_BINDING_STATUS.LINKED,
                            accessTokenState: data?.access_token
                                ? ACCESS_TOKEN_STATUS.VALID
                                : ACCESS_TOKEN_STATUS.INVALID,
                            email: data?.email ?? '',
                            name: data?.user_name ?? '',
                        },
                    }),
                );
            };
            const data = window.fjuser?.info?.account_link;
            if (data?.googleDrive?.isLink) {
                __updateSocialState(SOCIAL_KEY.GOOGLE_DRIVE, data.googleDrive);
            }
            if (data?.youtube?.isLink) {
                __updateSocialState(SOCIAL_KEY.YOUTUBE, data.youtube);
            }
        },
} as const;
